import { DatePicker } from '@mui/lab'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { TextField, Box, SxProps } from '@mui/material'
import InputLabel from '@mui/material/InputLabel'
import classNames from 'classnames'
import { es } from 'date-fns/locale'
import { useField } from 'formik'
import { t } from 'i18next'
import { CalendarIcon, AngleLeftIcon, AngleRightIcon } from 'src/components/shared/ui/Icons'
import { fieldsStyles as styles } from 'src/components/shared/ui/form/fields/fieldsStyles'
import { MIN_DATE } from 'src/constants/time'
import { theme } from 'src/providers/ThemeProvider'

type Props = {
  id: string
  label: string
  disabled?: boolean
  required?: boolean
  placeholder?: string
  disableFuture?: boolean
}

const stylesDatePicker: SxProps = {
  ...styles.input,

  '& .MuiOutlinedInput-input': {
    padding: 0,
    minHeight: '42px'
  },

  '&.error:not(.disabled) .MuiOutlinedInput-root': {
    borderBottom: 0
  },

  '& svg': {
    color: theme.palette.primary.main,
    fontSize: '18px'
  }
}

function WrapperCalendarIcon(): JSX.Element {
  return (
    <Box className="wrapperRightIcon" display="flex" justifyContent="center" alignItems="center">
      <CalendarIcon />
    </Box>
  )
}

function DatePickerField(props: Props): JSX.Element {
  const { id, label, disabled, required, placeholder = 'dd/mm/aaaa', disableFuture = false } = props
  const [field, meta, helpers] = useField(id)
  const inputClassNames = classNames({
    disabled,
    required,
    error: meta.error
  })

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
        <InputLabel id={id} className={inputClassNames}>
          {label}
        </InputLabel>
        <DatePicker
          mask="__/__/____"
          inputFormat="dd/MM/yyyy"
          className={inputClassNames}
          disableFuture={disableFuture}
          components={{
            OpenPickerIcon: WrapperCalendarIcon,
            LeftArrowIcon: AngleLeftIcon,
            RightArrowIcon: AngleRightIcon
          }}
          value={field.value}
          minDate={new Date(MIN_DATE)}
          onChange={value => helpers.setValue(value ?? null, true)}
          renderInput={params => (
            <TextField
              className={inputClassNames}
              sx={stylesDatePicker}
              {...params}
              inputProps={{
                ...params.inputProps,
                placeholder
              }}
            />
          )}
          rightArrowButtonText={t('actions.next')}
          leftArrowButtonText={t('actions.previous')}
          cancelText={t('actions.cancel')}
          clearText={t('actions.clear')}
          okText={t('actions.ok')}
          todayText={t('actions.today')}
        />
        <Box component="span" sx={styles.helpText}>
          {meta.error}
        </Box>
      </LocalizationProvider>
    </>
  )
}

export default DatePickerField
